import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Flame, Loader2, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/sports-betting/build-tickets`;
const LG = { nba: 'NBA', nfl: 'NFL', cfb: 'College Football' };

// One clean numbered list, sized to fit a phone screenshot.
const ShotList = ({ res, league }) => {
  const t = res.ticket;
  const hot = res.mode === 'hot';
  return (
    <div className={`rounded-xl border-2 p-3 space-y-1.5 ${hot ? 'border-orange-500/70 bg-orange-950/20' : 'border-sky-500/70 bg-sky-950/20'}`} data-testid={`shot-list-${res.mode}-${league}`}>
      <div className="flex items-baseline justify-between gap-2">
        <p className={`text-base font-black ${hot ? 'text-orange-300' : 'text-sky-300'}`}>{LG[league]} · {t.title} · {t.legs.length} legs</p>
        <p className="text-[11px] text-zinc-400">${t.stake} · all hit <b className="text-amber-300" data-testid={`shot-chance-${res.mode}`}>{(t.chance_all_hit * 100).toFixed(1)}%</b></p>
      </div>
      <p className="text-[11px] text-zinc-400">{t.why}</p>
      {res.note && <p className="text-[11px] text-amber-300" data-testid={`shot-note-${res.mode}`}>{res.note}</p>}
      <ol className="divide-y divide-zinc-800/80">
        {t.legs.map((l, i) => (
          <li key={`${l.player || l.team}-${l.stat}`} className="flex items-baseline gap-2 py-1" data-testid={`shot-leg-${res.mode}-${i}`}>
            <span className="w-5 shrink-0 text-right text-xs font-bold text-zinc-500">{i + 1}.</span>
            <span className="min-w-0 flex-1">
              <span className="text-[13px] font-bold text-white">{l.player ? `${l.player} — ${l.text}` : l.text}</span>
              <span className="block text-[10px] text-zinc-500">{l.game}{l.now ? ` · ${l.now}` : ''}</span>
            </span>
            <span className="text-xs font-black text-emerald-300">{Math.round(l.prob * 100)}%</span>
          </li>
        ))}
      </ol>
    </div>
  );
};

export const HotFreshButtons = ({ league, stake }) => {
  const [busy, setBusy] = useState('');
  const [res, setRes] = useState(null);
  const run = async (mode) => {
    setBusy(mode);
    try {
      const r = (await axios.get(`${API}/${league}/${mode}`, { params: { stake } })).data;
      if (r.ticket) setRes(r);
      else toast.info(r.note || 'Nothing to build right now');
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setBusy('');
  };
  const btn = 'inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-black disabled:opacity-50';
  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <button type="button" onClick={() => run('hot')} disabled={!!busy || stake <= 0} data-testid={`builder-hot-${league}`} className={`${btn} bg-orange-500 text-black hover:bg-orange-400`}>
          {busy === 'hot' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Flame className="w-3.5 h-3.5" />}Hot right now
        </button>
        <button type="button" onClick={() => run('fresh')} disabled={!!busy || stake <= 0} data-testid={`builder-fresh-${league}`} className={`${btn} bg-sky-500 text-black hover:bg-sky-400`}>
          {busy === 'fresh' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}Fresh players
        </button>
      </div>
      <p className="text-[10px] text-zinc-500">Hot: players in live games already on pace. Fresh: 8–20 legs with nobody from your open tickets.</p>
      {res?.ticket && <ShotList res={res} league={league} />}
    </div>
  );
};
